import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { api } from '@core/api';
import { captureAuthError, isExpectedClientTransportError } from '@core/sentry/utils';
import { safeSessionStorage } from '@shared/utils/safeStorage';
import type { OAuthPrefill } from '../types';
import { routeOAuthResponse, type OAuthBackendResponse } from '../utils/routeOAuthResponse';

/**
 * Facebook OAuth — popup authorization code flow.
 * Matches the Satellizer flow: open popup → Facebook redirects with ?code= → send code to backend.
 * The backend exchanges the code for an access token server-side.
 */

interface UseFacebookLoginOptions {
  /** Called when Facebook returned a JWT — the user is already registered. */
  onSuccess: (token: string, email: string) => Promise<void> | void;
  /**
   * Called when Facebook authenticated but the user is not yet in Meitre.
   * The backend response shape is `{ uid, email, fullname }` with no token.
   * The host page should navigate to the signup form prefilled with this
   * data. Facebook-specific: if the user does not share an email with Meitre
   * (privacy setting), we cannot route to signup and show a dedicated error
   * instead — signup requires email as the identity key.
   *
   * Optional; omitted callers get the same error path as a broken response.
   */
  onNeedsSignup?: (prefill: OAuthPrefill) => void;
}

/**
 * Message sent from the OAuth popup back to the opener window.
 */
interface OAuthPopupMessage {
  type: 'meitre-oauth-callback';
  code?: string;
  error?: string;
}

export function useFacebookLogin({ onSuccess, onNeedsSignup }: UseFacebookLoginOptions) {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initRef = useRef(false);

  // Stable refs for callback props — see useGoogleSignIn for the rationale.
  const onSuccessRef = useRef(onSuccess);
  const onNeedsSignupRef = useRef(onNeedsSignup);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onNeedsSignupRef.current = onNeedsSignup;
  }, [onSuccess, onNeedsSignup]);

  const appId = import.meta.env.VITE_FACEBOOK_APP_ID;
  const redirectUri = window.location.origin + '/';

  // Mark as ready if we have an app ID (no SDK preload needed for popup code flow)
  useEffect(() => {
    if (!appId || initRef.current) return;
    initRef.current = true;
    setIsReady(true);
  }, [appId]);

  const signIn = useCallback(async () => {
    if (!appId) {
      setError('Facebook Login is not available');
      return;
    }

    setError(null);
    setIsLoading(true);

    // Save context for in-app browser fallback (no popup, navigates in same window)
    safeSessionStorage.setItem('oauth_context', JSON.stringify({
      returnUrl: window.location.href,
      provider: 'facebook',
    }));

    try {
      // Open Facebook OAuth dialog in a popup (same as Satellizer)
      const width = 580;
      const height = 520;
      const left = Math.round((screen.width - width) / 2);
      const top = Math.round((screen.height - height) / 2);

      const fbAuthUrl =
        `https://www.facebook.com/v19.0/dialog/oauth` +
        `?client_id=${appId}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&scope=email` +
        `&response_type=code`;

      const popup = window.open(
        fbAuthUrl,
        'meitre_fb_auth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
      );

      if (!popup) {
        setError('Popup blocked. Please allow popups for this site.');
        setIsLoading(false);
        return;
      }

      // Listen for the code from the popup via postMessage. Three ways this
      // promise can settle: (1) postMessage from the popup callback page,
      // (2) user closes the popup manually (polled every 500ms), or (3)
      // safety timeout after 2 minutes. We track the subscriptions so the
      // first exit cleans up the others — without that, the 2-minute
      // timer kept `popup`, `handler` and `closedCheck` alive in closure
      // memory per auth attempt. The `settled` flag guards against the
      // tiny race window where two signals fire in the same tick.
      const code = await new Promise<string>((resolve, reject) => {
        let settled = false;
        const cleanup = () => {
          window.removeEventListener('message', handler);
          clearInterval(closedCheck);
          clearTimeout(timeoutId);
        };
        const settle = (fn: () => void) => {
          if (settled) return;
          settled = true;
          cleanup();
          fn();
        };

        const handler = (event: MessageEvent<OAuthPopupMessage>) => {
          // Only accept messages from our own origin
          if (event.origin !== window.location.origin) return;
          if (event.data?.type !== 'meitre-oauth-callback') return;

          if (event.data.code) {
            settle(() => resolve(event.data.code!));
          } else {
            settle(() =>
              reject(new Error(event.data.error || 'No authorization code received')),
            );
          }
        };

        // Also detect if user closes popup manually
        const closedCheck = setInterval(() => {
          if (popup.closed) settle(() => reject(new Error('popup_closed')));
        }, 500);

        window.addEventListener('message', handler);

        const timeoutId = setTimeout(() => {
          settle(() => {
            if (!popup.closed) popup.close();
            reject(new Error('timeout'));
          });
        }, 120_000);
      });

      // Send authorization code to backend. Two possible response shapes:
      //   - Existing user → `{ token, email }` (logged in directly)
      //   - New user      → `{ uid, email?, fullname }` (needs signup prefill)
      const result = await api.post<OAuthBackendResponse>(
        '/auth/facebook',
        { code, clientId: appId, redirectUri },
        { skipAuth: true },
      );

      const action = routeOAuthResponse('facebook', result);
      switch (action.kind) {
        case 'success':
          await onSuccessRef.current(action.token, action.email);
          return;
        case 'needs-signup':
          if (!onNeedsSignupRef.current) break;
          onNeedsSignupRef.current(action.prefill);
          return;
        case 'facebook-missing-email':
          // User has a FB account without a public email (privacy setting
          // or email-less account). We cannot register them — email is the
          // backend identity key. Show a provider-specific error.
          captureAuthError('auth.facebook_missing_email', {
            hasFullname: action.hasFullname,
          });
          setError('Facebook did not share your email. Please try another sign-in method.');
          return;
        case 'error':
          break; // handled below
      }

      // Fall-through for error cases.
      captureAuthError('auth.oauth_response_missing_token', {
        provider: 'facebook',
        hasEmail: action.kind === 'error' ? action.hasEmail : false,
        hasUserId: action.kind === 'error' ? action.hasUserId : false,
        hasUid: action.kind === 'error' ? action.hasUid : false,
        responseKeys: action.kind === 'error' ? action.responseKeys : [],
      });
      setError('Facebook login did not complete. Please try another method.');
    } catch (err) {
      if (err instanceof Error && (err.message === 'popup_closed' || err.message === 'timeout')) {
        // User closed popup — not an error
        setIsLoading(false);
        return;
      }
      if (!isExpectedClientTransportError(err)) {
        Sentry.captureException(err, { tags: { auth: 'facebook' } });
      }
      setError('Facebook login failed. Please try another method.');
    } finally {
      setIsLoading(false);
    }
  }, [appId, redirectUri]);

  return { signIn, isReady: isReady && !!appId, isLoading, error };
}
